/**
 * CorelDRAW (.cdr) helpers for MVK floorplans.
 *
 * Modern CDR (X6+) is a ZIP: application/x-vnd.corel.zcf.draw.document+zip.
 * Geometry lives in binary content/data/page*.dat — we convert to SVG via
 * LibreOffice/libcdr on the API host. textinfo.xml still lists booth labels.
 */

export const CDR_ZCF_MIMETYPE = "application/x-vnd.corel.zcf.draw.document+zip";

export function isCdrFileName(name: string): boolean {
  return /\.cdr$/i.test(String(name ?? "").trim());
}

/** ZIP local-file header + Corel ZCF mimetype entry. */
export function looksLikeCdrZip(bytes: Uint8Array | ArrayBuffer): boolean {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (u8.length < 4) return false;
  // PK\x03\x04
  if (u8[0] !== 0x50 || u8[1] !== 0x4b || u8[2] !== 0x03 || u8[3] !== 0x04) {
    return false;
  }
  // Cheap scan for Corel mimetype string in the first 2KB.
  const head = new TextDecoder("latin1").decode(u8.subarray(0, Math.min(u8.length, 2048)));
  return (
    head.includes("application/x-vnd.corel.zcf.draw.document") ||
    head.includes("application/x-vnd.corel.draw.document") ||
    head.includes("corel")
  );
}

export function normalizeBoothCodeFromCdrText(raw: string): string | null {
  const t = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[–—−]/g, "-")
    .replace(/\s+/g, "")
    .replace(/-/g, "");
  if (!t || t.length > 8) return null;
  if (/^(19|20)\d{2}$/.test(t)) return null;
  // Pavilion codes like A301 / A10 — require ≥2 digits after letter(s).
  if (/^[A-Z]{1,2}\d{2,4}[A-Z]?$/.test(t)) return t;
  // Bare numeric booth labels (rare): 2–3 digits only.
  if (/^\d{2,3}$/.test(t)) return t;
  return null;
}

/** Parse booth codes from Corel META-INF/textinfo.xml (UTF-8). */
export function extractBoothCodesFromTextinfoXml(xml: string): string[] {
  const s = String(xml ?? "");
  if (!s) return [];
  const codes = new Set<string>();
  for (const m of s.matchAll(/<TextRun\b[^>]*>([^<]*)<\/TextRun>/gi)) {
    const code = normalizeBoothCodeFromCdrText(m[1] ?? "");
    if (code && /[A-Z]/.test(code)) codes.add(code);
  }
  // Fallback: bare text nodes if namespace form differs.
  if (codes.size === 0) {
    for (const m of s.matchAll(/>([A-Za-z]?\d{2,4}[A-Za-z]?)</g)) {
      const code = normalizeBoothCodeFromCdrText(m[1] ?? "");
      if (code && /[A-Z]/.test(code)) codes.add(code);
    }
  }
  return [...codes].sort();
}
